/**
 * PositionTable component for displaying positions with all columns.
 */

import { useState, useMemo, useCallback } from 'react';
import { Table, Spinner, Card, CardContent } from '@/components/ui';
import type { Column, SortDirection } from '@/components/ui';
import { formatCurrency, formatNumber, formatDate, formatPercent } from '@/utils/formatters';
import { useExpandState } from '@/hooks/useExpandState';
import { ExpandToggle } from './ExpandToggle';
import { TradeSubTable } from './TradeSubTable';
import type { Position } from '@/types';

/** Position with calculated size percentage */
export interface PositionWithSize extends Position {
  /** Position size as percentage of account net liquidity */
  size_percent: number;
}

interface PositionTableProps {
  /** Positions to display */
  positions: Position[];
  /** Account net liquidity for calculating position size % */
  netLiquidity?: number | null;
  /** Loading state */
  isLoading?: boolean;
  /** Error message */
  error?: string | null;
}

type SortKey =
  | 'symbol'
  | 'instrument_type'
  | 'quantity_direction'
  | 'quantity'
  | 'average_cost'
  | 'cost_basis'
  | 'size_percent'
  | 'realized_pnl'
  | 'position_status'
  | 'created_at'
  | 'closed_at';

/**
 * Render P&L value with color coding.
 */
function renderPnL(value: number): JSX.Element {
  const isPositive = value >= 0;
  const colorClass = isPositive ? 'text-green-600' : 'text-red-600';
  const prefix = isPositive ? '+' : '';
  return <span className={colorClass}>{prefix}{formatCurrency(value)}</span>;
}

/**
 * Render status badge.
 */
function renderStatusBadge(status: Position['position_status']): JSX.Element {
  const isActive = status === 'ACTIVE';
  const bgClass = isActive ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800';
  const label = isActive ? 'Open' : 'Closed';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${bgClass}`}>
      {label}
    </span>
  );
}

/**
 * Render date or dash for null values.
 */
function renderDateOrDash(date: string | undefined): string {
  return date ? formatDate(date) : '—';
}

/**
 * Enhanced position table with all columns and sorting.
 */
export function PositionTable({
  positions,
  netLiquidity = null,
  isLoading = false,
  error = null,
}: PositionTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('symbol');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = useCallback((key: string) => {
    if (key === sortKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key as SortKey);
      setSortDirection('asc');
    }
  }, [sortKey]);

  // Calculate position size percentages
  const positionsWithSize: PositionWithSize[] = useMemo(() => {
    return positions.map((position) => ({
      ...position,
      size_percent:
        netLiquidity && netLiquidity > 0
          ? (Math.abs(position.cost_basis) / netLiquidity) * 100
          : 0,
    }));
  }, [positions, netLiquidity]);

  const sortedPositions = useMemo(() => {
    const result = [...positionsWithSize];

    result.sort((a, b) => {
      let aVal: string | number | undefined;
      let bVal: string | number | undefined;

      switch (sortKey) {
        case 'symbol':
          aVal = a.symbol;
          bVal = b.symbol;
          break;
        case 'instrument_type':
          aVal = a.instrument_type;
          bVal = b.instrument_type;
          break;
        case 'quantity_direction':
          aVal = a.quantity_direction;
          bVal = b.quantity_direction;
          break;
        case 'quantity':
          aVal = a.quantity;
          bVal = b.quantity;
          break;
        case 'average_cost':
          aVal = a.average_cost;
          bVal = b.average_cost;
          break;
        case 'cost_basis':
          aVal = a.cost_basis;
          bVal = b.cost_basis;
          break;
        case 'size_percent':
          aVal = a.size_percent;
          bVal = b.size_percent;
          break;
        case 'realized_pnl':
          aVal = a.realized_pnl;
          bVal = b.realized_pnl;
          break;
        case 'position_status':
          aVal = a.position_status;
          bVal = b.position_status;
          break;
        case 'created_at':
          aVal = a.created_at;
          bVal = b.created_at;
          break;
        case 'closed_at':
          aVal = a.closed_at ?? '';
          bVal = b.closed_at ?? '';
          break;
        default:
          return 0;
      }

      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bVal === undefined) return sortDirection === 'asc' ? -1 : 1;

      // Compare values
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });

    return result;
  }, [positionsWithSize, sortKey, sortDirection]);

  // Derive visible IDs from sorted/filtered data for expand state management
  const visibleIds = useMemo(
    () => sortedPositions.map((p) => p.position_id),
    [sortedPositions]
  );

  const { toggle, isExpanded } = useExpandState({ visibleIds });

  // Expand toggle column prepended to the table
  const expandColumn: Column<PositionWithSize> = useMemo(
    () => ({
      key: 'expand',
      header: '',
      render: (position) => (
        <ExpandToggle
          isExpanded={isExpanded(position.position_id)}
          symbol={position.symbol}
          onToggle={() => toggle(position.position_id)}
        />
      ),
    }),
    [isExpanded, toggle]
  );

  const columns: Column<PositionWithSize>[] = useMemo(
    () => [
      {
        key: 'symbol',
        header: 'Symbol',
        sortable: true,
        render: (position) => (
          <span className="font-medium">{position.symbol}</span>
        ),
      },
      {
        key: 'instrument_type',
        header: 'Type',
        sortable: true,
        render: (position) => position.instrument_type,
      },
      {
        key: 'quantity_direction',
        header: 'Direction',
        sortable: true,
        render: (position) => (
          <span className={position.quantity_direction === 'Short' ? 'text-red-600' : ''}>
            {position.quantity_direction}
          </span>
        ),
      },
      {
        key: 'quantity',
        header: 'Qty',
        sortable: true,
        render: (position) => formatNumber(Math.abs(position.quantity)),
      },
      {
        key: 'average_cost',
        header: 'Avg Cost',
        sortable: true,
        render: (position) => formatCurrency(position.average_cost),
      },
      {
        key: 'cost_basis',
        header: 'Cost Basis',
        sortable: true,
        render: (position) => formatCurrency(position.cost_basis),
      },
      {
        key: 'size_percent',
        header: 'Size %',
        sortable: true,
        render: (position) => (
          <span className={position.size_percent > 10 ? 'font-medium text-amber-600' : ''}>
            {formatPercent(position.size_percent)}
          </span>
        ),
      },

      {
        key: 'realized_pnl',
        header: 'Realized P&L',
        sortable: true,
        render: (position) => renderPnL(position.realized_pnl),
      },
      {
        key: 'position_status',
        header: 'Status',
        sortable: true,
        render: (position) => renderStatusBadge(position.position_status),
      },
      {
        key: 'created_at',
        header: 'Created',
        sortable: true,
        render: (position) => formatDate(position.created_at),
      },
      {
        key: 'closed_at',
        header: 'Closed',
        sortable: true,
        render: (position) => renderDateOrDash(position.closed_at),
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-red-600">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table
        data={sortedPositions}
        columns={columns}
        keyExtractor={(position) => position.position_id}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        emptyMessage="No positions found"
        prependColumn={expandColumn}
        onRowClick={(position) => toggle(position.position_id)}
        renderExpandedContent={(position) =>
          isExpanded(position.position_id) ? (
            <TradeSubTable
              trades={position.trades ?? []}
              symbol={position.symbol}
              tradeSource={position.trade_source}
            />
          ) : null
        }
      />
    </div>
  );
}

// Export sorting function for testing
export function sortPositions(
  positions: Position[],
  sortKey: SortKey,
  sortDirection: SortDirection
): Position[] {
  const result = [...positions];

  result.sort((a, b) => {
    let aVal: string | number | undefined;
    let bVal: string | number | undefined;

    switch (sortKey) {
      case 'symbol':
        aVal = a.symbol;
        bVal = b.symbol;
        break;
      case 'quantity':
        aVal = a.quantity;
        bVal = b.quantity;
        break;
      case 'cost_basis':
        aVal = a.cost_basis;
        bVal = b.cost_basis;
        break;
      default:
        return 0;
    }

    if (aVal === undefined && bVal === undefined) return 0;
    if (aVal === undefined) return sortDirection === 'asc' ? 1 : -1;
    if (bVal === undefined) return sortDirection === 'asc' ? -1 : 1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    return sortDirection === 'asc'
      ? (aVal as number) - (bVal as number)
      : (bVal as number) - (aVal as number);
  });

  return result;
}
